import { Injectable } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { DataSecurityLevel as otelDataSecurityLevel, KnownErrorInstrumentationEvents, KnownFeatureNames } from '@office-start/telemetry-types';
import { ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';

import {
    InstrumentationParam,
    InstrumentationService,
    KnownInstrumentationEvents
} from '../../shared/instrumentation/instrumentation.service';
import { FileItem } from '../../shared/models/file-item.model';
import { EventService } from '../../shared/services/event.service';
import { LocalAppLauncherService } from '../../shared/services/local-app-launcher.service';
import { Performance } from '../../shared/services/performance.service';
import { LaunchAction, UrlLauncherService } from '../../shared/services/url-launcher.service';
import { AngularEvents } from '../../shared/shared-context/angular-events';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { getNativeUrlForFile } from '../../shared/utilities/native-app-url.utility';
import { FileSystemAccessPoint } from '../../upload/upload-local-file/upload-local-file.service';
import { PwaConfigService } from './pwa-config.service';
import { PwaNativeBridgeService } from './pwa-native-bridge.service';

@Injectable()
export class PwaLocalAppLauncherService implements LocalAppLauncherService {
    private readonly correlationId: string;
    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly eventService: EventService,
        private readonly instrumentationService: InstrumentationService,
        private readonly pwaConfigService: PwaConfigService,
        private readonly pwaNativeBridgeService: PwaNativeBridgeService,
        private readonly urlLauncherService: UrlLauncherService
    ) {
        this.correlationId = this.clientStartupConfigService.config.sharedClientStartupContext.correlationId;
    }

    // TODO: Remove isExcelCreateNew once native Excel supports 0-byte create flow.
    public async launchOfficeApp(
        appName: string,
        filePath: string = '',
        preLaunchCallback: () => void = null,
        isExcelCreateNew: boolean = false
    ): Promise<boolean> {
        const launchLocalAppStart = Performance.now();
        if (preLaunchCallback) {
            preLaunchCallback();
        }

        if (
            this.pwaConfigService.config.PwaStartPageEnabled &&
            this.capabilitiesService.capabilities.startPageApps &&
            this.capabilitiesService.capabilities.startPageApps.has(FileItem.fileTypeNameToFileType(appName)) &&
            filePath === '' &&
            !isExcelCreateNew
        ) {
            return false;
        }

        // TODO: Consider adding logic below to url-parser.utility
        if (filePath) {
            filePath = decodeURI(filePath);
            const pos = filePath.indexOf('?');
            filePath = pos >= 0 ? filePath.substring(0, pos) : filePath;
        }

        // This section is to mitigate the app launching issue starts to happen since Windows version 19041.331.
        if (this.pwaConfigService.config.officeUriProtocolEnabled && appName && filePath !== '' && !filePath.startsWith('https://1drv.ms')) {
            const url = (() => {
                try {
                    return new URL(filePath);
                } catch (e) {
                    return new URL('file://' + filePath);
                }
            })();

            const nativeUrl = getNativeUrlForFile(FileItem.fileTypeNameToFileType(appName), url.toString(), this.correlationId);
            const launched = await (async () => {
                if (!nativeUrl) {
                    return false;
                }
                // If app is not installed, or launch via protocol handler failed, then return false and fallback to the native bridge launchOfficeApp.
                const params = {};
                let succeeded = false;
                try {
                    const appInfo = await this.pwaNativeBridgeService.getInstalledApp(appName);
                    if (!appInfo) {
                        return false;
                    }

                    if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                        await import('@office-start/launcher-service').then(
                            launchSvc =>
                                launchSvc.launchUrl(nativeUrl, {action: 'LaunchProtocol'})
                        );
                    } else {
                        await this.urlLauncherService.openUrl(nativeUrl, LaunchAction.LaunchProtocol);
                    }

                    succeeded = true;
                } catch (error) {
                    params[InstrumentationParam.Error] = error?.toString() ?? '';
                }

                params[InstrumentationParam.Action] = 'AppsProtocol';
                params[InstrumentationParam.State] = succeeded ? 'Success' : 'Failed';
                params[InstrumentationParam.Latency] = Math.ceil(performance.now() - launchLocalAppStart).toString();

                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.LaunchLocalApp, params, [], DataSecurityLevel.Enhanced);
                logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.LaunchLocalApp, params }, otelDataSecurityLevel.Enhanced);

                return succeeded;
            })();

            if (launched) {
                return true;
            }
        }

        return this.pwaNativeBridgeService.launchOfficeApp(appName, filePath, launchLocalAppStart);
    }

    public async launchOfficeAppWithTemplate(appName: string, templateUrl: string): Promise<boolean> {
        if (!appName || !templateUrl) {
            return false;
        }

        const nativeUrl = getNativeUrlForFile(FileItem.fileTypeNameToFileType(appName), templateUrl, this.correlationId, true);
        if (!nativeUrl) {
            // if command is not supported by protocol handler, then return false.
            return false;
        }

        try {
            const appInfo = await this.pwaNativeBridgeService.getInstalledApp(appName);
            if (appInfo) {
                if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                    await import('@office-start/launcher-service').then(
                        launchSvc =>
                            launchSvc.launchUrl(nativeUrl, {action: 'CreateFileWithTemplate'})
                    );
                } else {
                    await this.urlLauncherService.openUrl(nativeUrl, LaunchAction.CreateFileWithTemplate);
                }

                return true;
            }
        } catch (error) {
            // If app is not installed, or launch via protocol handler failed, then return false and fallback to the create new empty file flow.
        }

        return false;
    }

    public async openPathInExplorer(path: string): Promise<void> {
        if (this.capabilitiesService.capabilities.isPwaInUwp) {
            return new Promise<void>((resolve, reject) => {
                Windows.Storage.StorageFolder.getFolderFromPathAsync(path)
                    .then(
                        folder => {
                            if (!folder) {
                                throw new Error('GetFolderFromPath returned null');
                            }
                            return Windows.System.Launcher.launchFolderAsync(folder).then(
                                success => {
                                    if (!success) {
                                        throw new Error('Failed to open folder');
                                    }
                                    resolve();
                                },
                                () => {
                                    throw new Error('LaunchFolder exception');
                                }
                            );
                        },
                        () => {
                            this.eventService.broadcastEvent({
                                name: AngularEvents.needAccessToFileSystem,
                                args: [FileSystemAccessPoint.AllowFilesSystemAccessOpenFileLocation]
                            });
                            throw new Error('GetFolderFromPath exception');
                        }
                    )
                    .then(null, (error: Error) => {
                        const params = {};
                        params[InstrumentationParam.Error] = error ? error.message : '';
                        this.instrumentationService.logGenericEvent(
                            KnownInstrumentationEvents.LaunchFileExplorerFailed,
                            params,
                            [],
                            DataSecurityLevel.Enhanced
                        );
                        logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownErrorInstrumentationEvents.LaunchFileExplorerFailed, params }, otelDataSecurityLevel.Enhanced);
                        reject();
                    });
            });
        }

        return Promise.reject();
    }

    public async shouldOpenInOfficeApp(appName: string): Promise<boolean> {
        return this.pwaNativeBridgeService
            .getInstalledApp(appName)
            .then(appInfo => !!appInfo)
            .catch(() => false);
    }
}
